import { useEffect, useState } from 'react';
import { MessageContext } from './MessageContext';

export type ProductWindow = {
  origin: string;
  frameWindow: Window;
  id: string;
  acknowledged: boolean;
  logoutUrl: string;
};

export enum MESSAGES {
  CLIENT_READY = 'CLIENT_READY',
  INIT_PRODUCT = 'INIT_PRODUCT',
  INIT_ACKNOWLEDGE = 'INIT_ACKNOWLEDGE',
  LOGOUT = 'LOGOUT',
  CLOSE_APP = 'CLOSE_APP',
  WINDOW_OPEN = 'WINDOW_OPEN',
  UNIFY_PREFERENCE_CHANGED = 'UNIFY_PREFERENCE_CHANGED',
  NOTIFICATION_RECEIVED = 'NOTIFICATION_RECEIVED',
  APP_LOCATION_CHANGED = 'APP_LOCATION_CHANGED'
}

export enum LOGOUT_STATUS {
  LOGOUT_USERCONFIRM = 'LOGOUT_USERCONFIRM',
  LOGOUT_INIT = 'LOGOUT_INIT',
  LOGOUT_PROGRESS = 'LOGOUT_PROGRESS',
  APPS_COMPLETE = 'APPS_COMPLETE',
  NONE = 'NONE',
}

export enum SLO_MODE {
  MESSAGING = 'MESSAGING',
  END_POINTS = 'END_POINTS',
}

export const MessageProvider = (props: any) => {
  const unifyOrigin = document.URL;
  const [productWindows, setProductWindows] = useState<ProductWindow[]>([]);
  const [logoutStatus, setLogoutStatus] = useState(LOGOUT_STATUS.NONE);
  const [sloMode, setSloMode] = useState(SLO_MODE.MESSAGING);
  const [subscriberMap, setSubscriberMap] = useState<any>({});
  const [activeModel, setActiveModel] = useState<any>();

  const initClient = () => {
    productWindows.forEach(prodWin => prodWin.frameWindow.postMessage({ 
      id: prodWin.id, 
      action: MESSAGES.INIT_PRODUCT, 
      origin: unifyOrigin }, 
      prodWin.origin));
  }

  const notifyMessage = (msg: { action: string, payload: any }) => {
    productWindows.forEach((prodWin) => {
        prodWin.frameWindow.postMessage(
          {
            id: prodWin.id,
            action: msg.action,
            payload: msg.payload,
            origin: unifyOrigin,
          },
          prodWin.origin
        );
      });
  }

  function extractPathFromUrl(url: string): string {
    try {
      const p = new URL(url);
      return `${p.pathname}${p.search}${p.hash}`;
    } catch { return ''; }
  }

  let contextUrl = window.location.href;
  const handleDeepLink = (evt: MessageEvent) => {
    const urlFromLocation = evt.data?.origin || evt.data?.value;
    const { id } = evt.data;
    if (typeof urlFromLocation === 'string' && urlFromLocation.length > 0) {
      const newPath = extractPathFromUrl(urlFromLocation);
      if (newPath) {
        const dpLink = urlFromLocation;
        const activePane = localStorage.getItem('activePane');
        if (activePane === id) {
          let linkPath = window.location.pathname;
          linkPath = window.location.pathname.indexOf('/dp/') >= 0 ? (window.location.pathname.split('/dp/')[0] || '') : linkPath;
          linkPath = window.location.pathname.indexOf('/bdp') >= 0 ? (window.location.pathname.split('/bdp')[0] || '') : linkPath;
          contextUrl = dpLink
          window.history.replaceState({}, '', linkPath + '/bdp' + btoa(dpLink));
        }
      }
    }
  }

  /***
   * Initializing the message listener from loaded apps, so far we are listening LOGOUT action,
   * and initiating the other applications to logout.
   */
  const messageHandler = (evt: MessageEvent) => {
    try {
      const { id, action, payload } = evt.data;
      if (id) {
        switch (action) {
          case MESSAGES.CLIENT_READY:
            initClient();
            break;

          case MESSAGES.INIT_ACKNOWLEDGE:
            updateProductAcknowledge(id);
            break;

          case MESSAGES.LOGOUT:
            setLogoutStatus(LOGOUT_STATUS.LOGOUT_INIT);
            break;
          
          case MESSAGES.WINDOW_OPEN:
            openWindow(payload.url, id);
            break;

          case MESSAGES.APP_LOCATION_CHANGED: 
            handleDeepLink(evt);
            break;

          default:
            notifyMessage({ action, payload });
            break;
        };
        // notify subscribers
        Object.keys(subscriberMap).map((key) => {  return subscriberMap[key](evt) });
      }
    }
    catch (err: any) {
      console.warn('Unify Messaging - messageHandler Error', err.stackTrace)
    }
  };

  const openWindow = (url:string, id:string) => {
    const exWindow = window.open(url, id, `left=0,top=200,width=${window.innerWidth},height=${window.innerHeight - 110}`);
    window.unifyClient = {
      modelCallBack: (data: string) => {
        console.log('Model URL', data);
        notifyMessage({ action: 'MODEL_REDIRECT', payload: { url: data }});
        closeWindow();
      },
      activeModel: exWindow
    }
    setActiveModel(exWindow);
  };

  const closeWindow = () => {
    activeModel?.close();
    window.unifyClient.activeModel.close();
    setActiveModel(null);
  }

  const updateProductAcknowledge = (id: string) => {
    const prodWIndow = productWindows.filter((prod) => prod.id === id)[0];
    prodWIndow && (prodWIndow.acknowledged = true);
  };

  /***
   * This method is called when app/workflow loaded into iFrame,
   * The frame information is stored into loaded frames list.
   * INIT message is sent to loaded app, and the loaded app will acknoledge with INIT_ACKNOWLEDGE
   * message,
   */
  const addProduct = (prod: ProductWindow) => {
    try {
      const { id, origin } = prod;
      const prodWindows = [...productWindows, prod];
      const prodWin = productWindows.filter((win) => win.id === id)[0];
      if (prodWin) {
        prodWin.origin = origin
      }
      else {
        setProductWindows(prodWindows);
      }

      /** 
      if (sloMode === SLO_MODE.MESSAGING) {
        prod?.frameWindow?.postMessage(
          { id, action: MESSAGES.INIT_PRODUCT, origin: unifyOrigin },
          origin
        );
      }
      */
    }
    catch (err: any) {
      console.warn('Unify Messaging - addProduct Error', err.stackTrace);
    }
  };

  const removeProduct = (prod: ProductWindow) => {
    const openedFrames = productWindows?.filter((win) => win.id !== prod.id);
    setProductWindows(openedFrames);
  };

  const messagingLogout = () => {
    /* if message is triggered by any loaded app, then exempt that app from sending LOGOUT message,
     *  because it's logout started already after its message to Unify.
     */
    try {
      notifyMessage({ action: MESSAGES.LOGOUT, payload: null });
      setLogoutStatus(LOGOUT_STATUS.LOGOUT_PROGRESS);
      setTimeout(
        () => setLogoutStatus(LOGOUT_STATUS.APPS_COMPLETE),
        productWindows.length > 0 ? 5000 : 0
      );
    }
    catch (err: any) {
      console.warn('Unify Messaging - messagingLogout', err.stackTrace)
    }
  };

  const endPointLogout = () => {
    try {
      const endPointsCount = productWindows.filter(
        (prod) => prod.logoutUrl && prod.logoutUrl !== ''
      ).length;
      setLogoutStatus(LOGOUT_STATUS.LOGOUT_PROGRESS);
      productWindows.forEach((product) => {
        product.logoutUrl &&
          ((document.getElementById(product.id) as HTMLIFrameElement).src =
            product.logoutUrl);
      });

      setTimeout(
        () => setLogoutStatus(LOGOUT_STATUS.APPS_COMPLETE),
        endPointsCount > 0 ? 5000 : 0
      );
    }
    catch (err: any) {
      console.warn('Unify Messaging - messagingLogout', err.stackTrace)
    }
  };

  /***
   * This method is invoked when loggout triggred and also  LOGOUT messsage is received from
   * any of the loaded app, so that all loaded apps informed to LOGOUT message to forse their logout process.
   */
  const logoutProducts = () => {
    sloMode === SLO_MODE.MESSAGING ? endPointLogout() : messagingLogout();
  };

  const subscribe = ({ key, listener }: { key: string, listener: Function }) => {
    setSubscriberMap({ ...subscriberMap, [key]: listener });
  }

  const unSubscribe = ({ key }: { key: string }) => {
    delete subscriberMap[key];
    setSubscriberMap({ ...subscriberMap });
  }

  useEffect(() => {
    const msgListener = function (evt: any) {
      messageHandler(evt)
    }
    if (sloMode === SLO_MODE.MESSAGING) {
      window.addEventListener('message', msgListener);
    }
    // Always return cleanup function to prevent listener leaks
    return () => {
      if (sloMode === SLO_MODE.MESSAGING) {
        window.removeEventListener('message', msgListener);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sloMode, productWindows]);

  return (
    <MessageContext.Provider
      value={{
        logoutProducts,
        addProduct,
        removeProduct,
        setLogoutStatus,
        setSloMode,
        logoutStatus,
        openedProducts: productWindows,
        subscribe,
        unSubscribe,
        contextUrl,
      }}
    >
      {props.children}
    </MessageContext.Provider>
  );
};
